import { useState, useEffect, useContext, useRef } from 'react';
import Base from './Base';
import Swal from 'sweetalert2';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import { Camera, Copy, ExternalLink, AlertTriangle, User, MapPin } from 'lucide-react';
import blankProfileImg from '../media/blank_profile_img.webp';
import API_BASE_URL from '../config/api';

const slugifyUsername = (val) =>
  val.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const UserProfile = () => {
  const { authToken } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [originalUsername, setOriginalUsername] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    contact_number: '',
    gender: 'Male',
    image: null,
    description: '',
  });
  const [addressData, setAddressData] = useState({
    name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Cleanup blob URL when preview changes or component unmounts
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  // Fetch profile and address
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, addressRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/user_profile/`, {
            headers: { Authorization: `Bearer ${authToken.access}` },
          }),
          axios.get(`${API_BASE_URL}/api/manage_address/`, {
            headers: { Authorization: `Bearer ${authToken.access}` },
          }),
        ]);

        const uname = profileRes.data.obj.username || '';
        setOriginalUsername(uname);
        setFormData({
          username: uname,
          email: profileRes.data.obj.email || '',
          contact_number: profileRes.data.obj.contact_number || '',
          gender: profileRes.data.obj.gender || 'Male',
          image: profileRes.data.obj.image || null,
          description: profileRes.data.description || '',
        });
        setAddressData({
          name: addressRes.data.name || '',
          phone_number: addressRes.data.phone_number || '',
          address: addressRes.data.address || '',
          city: addressRes.data.city || '',
          state: addressRes.data.state || '',
          country: addressRes.data.country || '',
          pincode: addressRes.data.pincode || '',
        });
      } catch (err) {
        console.error('Failed to fetch profile:', err);
        setError('Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [authToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      const url = URL.createObjectURL(file);
      setImagePreview(url);
      setFormData((prev) => ({ ...prev, image: file }));
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUsernameBlur = () => {
    setFormData((prev) => ({
      ...prev,
      username: slugifyUsername(prev.username),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const updatedData = new FormData();
    updatedData.append('username', formData.username);
    updatedData.append('email', formData.email);
    updatedData.append('contact_number', formData.contact_number);
    updatedData.append('gender', formData.gender);
    updatedData.append('description', formData.description);
    if (formData.image instanceof File) {
      updatedData.append('image', formData.image);
    }

    try {
      const profileRes = await axios.post(
        `${API_BASE_URL}/api/user_profile/`,
        updatedData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${authToken.access}`,
          },
        }
      );

      await axios.post(`${API_BASE_URL}/api/manage_address/`, addressData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken.access}`,
        },
      });

      const savedUsername = profileRes.data.obj.username || formData.username;
      setOriginalUsername(savedUsername);
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
        setImagePreview(null);
      }
      setFormData((prev) => ({
        ...prev,
        username: savedUsername,
        image: profileRes.data.obj.image || null,
      }));

      Swal.fire({
        title: 'Saved!',
        text: 'Profile and address updated successfully.',
        icon: 'success',
        confirmButtonColor: '#d45614',
      });
    } catch (err) {
      console.error('Profile update failed:', err);
      setError('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const displayImage =
    imagePreview ||
    (typeof formData.image === 'string' && formData.image
      ? `${API_BASE_URL}${formData.image}`
      : null) ||
    blankProfileImg;

  const usernameChanged =
    formData.username &&
    originalUsername &&
    slugifyUsername(formData.username) !== originalUsername;

  const storeUrl = `${slugifyUsername(formData.username) || 'yourstore'}.vasstr.com`;

  const copyStoreUrl = () => {
    navigator.clipboard.writeText(`https://${storeUrl}`).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'Store URL copied to clipboard',
        timer: 1500,
        showConfirmButton: false,
      });
    });
  };

  const inputClasses =
    'w-full border border-gray-300 rounded-[9px] px-4 py-3 text-[#2C1810] placeholder-gray-400 text-sm font-fell focus:outline-none focus:ring-2 focus:ring-[#2C1810]/10 focus:border-[#2C1810] transition-all duration-150 bg-white';
  const labelClasses = 'block text-sm font-medium text-gray-700 mb-2';

  return (
    <Base
      Content={
        <div className="min-h-screen bg-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-96 gap-3">
              <div className="w-8 h-8 border-3 border-[#d45614] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500 font-fell">Loading profile...</p>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
              {/* Page Title */}
              <h1 className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-8 font-fell">
                Brand Profile
              </h1>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Profile Photo Card */}
                <div className="bg-white border border-gray-200 rounded-[9px] shadow-sm p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    {/* Photo */}
                    <div
                      className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-full overflow-hidden cursor-pointer group border-2 border-[#C9A882]/40 shadow-md flex-shrink-0"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <img
                        src={displayImage}
                        alt="Brand"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-[#2C1810]/0 group-hover:bg-[#2C1810]/40 transition-all duration-300 flex items-center justify-center">
                        <Camera className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />

                    {/* Photo text */}
                    <div className="text-center sm:text-left">
                      <h3 className="text-lg font-semibold text-[#2C1810] font-fell mb-1">
                        Brand Logo
                      </h3>
                      <p className="text-sm text-gray-500 mb-3">
                        Click the photo to upload a new image. Use a square image for best results.
                      </p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm font-medium px-4 py-2 rounded-[9px] border border-[#d45614] text-[#d45614] hover:bg-[#d45614]/5 transition-colors"
                      >
                        Change Photo
                      </button>
                    </div>
                  </div>
                </div>

                {/* Store URL Card */}
                <div className="bg-[#F4ECE5] border border-[#C9A882]/30 rounded-[9px] p-5 sm:p-6">
                  <label className="block text-sm font-medium text-[#2C1810] mb-3 font-fell">
                    Your Store URL
                  </label>
                  <div className="flex items-center gap-2 px-4 py-3 bg-white rounded-[9px] border border-[#C9A882]/30 shadow-sm">
                    <span className="text-[#2C1810] font-semibold flex-1 truncate text-sm sm:text-base font-fell">
                      {storeUrl}
                    </span>
                    <button
                      type="button"
                      onClick={copyStoreUrl}
                      className="p-2 hover:bg-gray-100 rounded-[9px] transition-colors"
                      title="Copy URL"
                    >
                      <Copy className="w-4 h-4 text-[#2C1810]/60" />
                    </button>
                    <a
                      href={`https://${storeUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-100 rounded-[9px] transition-colors"
                      title="Visit store"
                    >
                      <ExternalLink className="w-4 h-4 text-[#2C1810]/60" />
                    </a>
                  </div>
                  {usernameChanged && (
                    <div className="flex items-start gap-2 mt-3 text-amber-700 bg-amber-50 px-3 py-2 rounded-[9px] border border-amber-200">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <p className="text-xs leading-relaxed">
                        Changing your username will change your store URL. Existing
                        links to your old URL will stop working.
                      </p>
                    </div>
                  )}
                </div>

                {/* Brand Details Card */}
                <div className="bg-white border border-gray-200 rounded-[9px] shadow-sm p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 rounded-full bg-[#F4ECE5] flex items-center justify-center">
                      <User className="w-4 h-4 text-[#2C1810]" />
                    </div>
                    <h2 className="text-xl font-semibold text-[#2C1810] font-fell">
                      Brand Details
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className={labelClasses}>Username</label>
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        onBlur={handleUsernameBlur}
                        className={inputClasses}
                        placeholder="your-brand-name"
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={inputClasses}
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className={labelClasses}>Phone Number</label>
                      <input
                        type="text"
                        name="contact_number"
                        value={formData.contact_number}
                        onChange={handleChange}
                        className={inputClasses}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className={inputClasses}
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelClasses}>About Your Brand</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      maxLength="1000"
                      className={`${inputClasses} resize-none`}
                      placeholder="Tell customers about your brand, what makes your designs special, and your inspiration..."
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1.5 text-right">
                      {formData.description.length}/1000
                    </p>
                  </div>
                </div>

                {/* Business Address Card */}
                <div className="bg-white border border-gray-200 rounded-[9px] shadow-sm p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 rounded-full bg-[#F4ECE5] flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-[#2C1810]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-[#2C1810] font-fell">
                        Business Address
                      </h2>
                      <p className="text-xs text-gray-500">Used for shipping and order returns</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className={labelClasses}>Full Name</label>
                      <input
                        type="text"
                        name="name"
                        value={addressData.name}
                        onChange={handleAddressChange}
                        className={inputClasses}
                        placeholder="Name for shipping label"
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>Phone Number</label>
                      <input
                        type="text"
                        name="phone_number"
                        value={addressData.phone_number}
                        onChange={handleAddressChange}
                        className={inputClasses}
                        placeholder="Contact number"
                      />
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className={labelClasses}>Street Address</label>
                    <textarea
                      name="address"
                      value={addressData.address}
                      onChange={handleAddressChange}
                      rows={2}
                      className={`${inputClasses} resize-none`}
                      placeholder="Full street address"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className={labelClasses}>City</label>
                      <input
                        type="text"
                        name="city"
                        value={addressData.city}
                        onChange={handleAddressChange}
                        className={inputClasses}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>State</label>
                      <input
                        type="text"
                        name="state"
                        value={addressData.state}
                        onChange={handleAddressChange}
                        className={inputClasses}
                        placeholder="State"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClasses}>Pincode</label>
                      <input
                        type="text"
                        name="pincode"
                        value={addressData.pincode}
                        onChange={handleAddressChange}
                        className={inputClasses}
                        placeholder="Pincode"
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>Country</label>
                      <input
                        type="text"
                        name="country"
                        value={addressData.country}
                        onChange={handleAddressChange}
                        className={inputClasses}
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-[9px] text-sm text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Submit */}
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full sm:w-auto bg-[#d45614] text-white text-sm font-medium px-8 py-3 rounded-[9px] hover:bg-[#a8430e] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 shadow-sm hover:shadow-md"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <a
                    href={`https://${storeUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#d45614] hover:text-[#a8430e] font-medium transition-colors flex items-center gap-1"
                  >
                    Preview Store <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </form>
            </div>
          )}
        </div>
      }
    />
  );
};

export default UserProfile;
